import { RANGE_FILTER_SECTIONS } from '@/constants/inventory';
import { NewInventoryFiltersContext } from '@/contexts/NewInventoryFiltersContext';
import { useDebounce } from '@/hooks/useDebounce';
import { rangeFormatters } from '@/tools/inventory';
import { FilterSection, ISectionOption, RangeFilterValue } from '@/types/inventory';
import Slider from 'rc-slider';
import { FocusEvent, useContext, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

const getRangeValues = (section: FilterSection, getSectionOptions: (section: FilterSection) => ISectionOption[]) => {
  if (!RANGE_FILTER_SECTIONS.has(section)) return;

  const values = getSectionOptions(section);

  if (values.length < 2) return;

  const min = Number(values[0]);
  const max = Number(values[values.length - 1]);

  if (isNaN(min) || isNaN(max)) return;

  return { min, max };
};

const defaultFormatters = {
  input: (value: number) => value.toString(),
  label: (value: number) => value.toString(),
};

interface WrappedInventoryFiltersRangeProps {
  section: FilterSection;
  className?: string;
  min: number;
  max: number;
  filterValue?: RangeFilterValue;
}

const WrappedInventoryFiltersRange = ({
  section,
  className,
  min,
  max,
  filterValue,
}: WrappedInventoryFiltersRangeProps) => {
  const { setRangeFilter } = useContext(NewInventoryFiltersContext);
  const minInputRef = useRef<HTMLInputElement>(null);
  const maxInputRef = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState<number[]>(filterValue ? [filterValue.min, filterValue.max] : [min, max]);

  const formatter = rangeFormatters[section] || defaultFormatters;

  const resetInputs = (values: number[]) => {
    if (minInputRef.current) {
      minInputRef.current.value = formatter.input(values[0]);
    }
    if (maxInputRef.current) {
      maxInputRef.current.value = formatter.input(values[1]);
    }
  };

  useEffect(() => {
    const newValue = filterValue ? [filterValue.min, filterValue.max] : [min, max];

    setValue(newValue);
    resetInputs(newValue);
  }, [filterValue]);

  const applyFilter = (values: number[]) => {
    if (values[0] === min && values[1] === max) {
      setRangeFilter(section, null);
      resetInputs([min, max]);
      setValue([min, max]);
      return;
    }

    setRangeFilter(section, {
      min: values[0],
      max: values[1],
    });
    setValue(values);
    resetInputs(values);
  };

  const onNewInputValue = (index: number, rawValue: number) => {
    let parsedValue = rawValue;
    if (isNaN(rawValue) || parsedValue < min || parsedValue > max) {
      parsedValue = index === 0 ? min : max;
    }

    const newValues = [...value];
    newValues[index] = parsedValue;
    newValues.sort((a, b) => a - b);

    applyFilter(newValues);
  };

  const [debouncedOnNewInputValue, { clearTimer }] = useDebounce(onNewInputValue, 1500);

  const getHandleInputChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsedValue = parseInt(e.target.value.replace(/\D/g, ''));

    if (isNaN(parsedValue)) {
      e.target.value = '';
      clearTimer();
      return;
    }

    e.target.value = formatter.input(parsedValue);
    debouncedOnNewInputValue(index, parsedValue);
  };

  const getHandleInputBlur = (index: number) => (e: FocusEvent<HTMLInputElement>) => {
    const rawValue = parseInt(e.target.value.replace(/\D/g, ''));

    clearTimer();
    onNewInputValue(index, rawValue);
  };

  return (
    <div className={twMerge('flex flex-col gap-4', className)}>
      <div className="flex items-center gap-1.5 xs:gap-4">
        <input
          type="text"
          className="py-2.5 px-1 xs:px-3 rounded border border-black-200 text-base flex-1 w-0 text-center"
          defaultValue={formatter.input(value[0])}
          onChange={getHandleInputChange(0)}
          onBlur={getHandleInputBlur(0)}
          ref={minInputRef}
          data-testid={`${section}-min-input`}
        />
        <span className="text-base">-</span>
        <input
          type="text"
          className="py-2.5 px-1 xs:px-3 rounded border border-black-200 text-base flex-1 w-0 text-center"
          defaultValue={formatter.input(value[1])}
          onChange={getHandleInputChange(1)}
          onBlur={getHandleInputBlur(1)}
          ref={maxInputRef}
          data-testid={`${section}-max-input`}
        />
      </div>
      <div className="flex flex-col gap-3" data-testid={`${section}-slider`}>
        <Slider
          range
          className="new-filter-slider"
          value={value}
          min={min}
          max={max}
          step={1}
          onChange={(values) => {
            if (!(values instanceof Array) || values.length !== 2) return;

            setValue(values);
          }}
          onChangeComplete={(values) => {
            if (!(values instanceof Array) || values.length !== 2) return;

            applyFilter(values);
          }}
        />
        <div className="flex justify-between font-medium">
          <span data-testid={`${section}-min-text`}>{formatter.label(value[0])}</span>
          <span data-testid={`${section}-max-text`}>{formatter.label(value[1])}</span>
        </div>
      </div>
    </div>
  );
};

interface InventoryFiltersRangeProps {
  section: FilterSection;
  className?: string;
}

export const InventoryFiltersRange = ({ section, className }: InventoryFiltersRangeProps) => {
  const { filters, getSectionOptions } = useContext(NewInventoryFiltersContext);

  const filterValue = filters[section];
  const rangeLimits = getRangeValues(section, getSectionOptions);

  if (filterValue instanceof Array || !rangeLimits) return null;

  return (
    <WrappedInventoryFiltersRange
      section={section}
      className={className}
      min={rangeLimits.min}
      max={rangeLimits.max}
      filterValue={filterValue}
    />
  );
};
